import { useEffect, useMemo, useState } from "react";
import { useAppContextController } from "context/AppContext";
import { useForm } from "react-hook-form";
import CustomCard from "components/CustomCard";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import { cardStyles as styles, customPanelCard } from "layouts/pages/customers/styles";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import MDBox from "components/MDBox";
import useYupValidationResolver from "hooks/useYupValidationResolver";
import { shiftJobSettingsSchema, shiftsInfoSchema } from "data/shiftInfoSchema";
import StaffingPoolModal from "layouts/pages/jobs/components/ShiftJobs/StaffingPool/StaffingPoolModal";
import { useMutation, useQuery, useQueryClient } from "react-query";
import updateJob from "layouts/pages/jobs/actions/updateJob";
import updateCompanyJob from "layouts/pages/companyjobs/actions/updateJob";
import { useSnackbar } from "notistack";
import isEqual from "lodash/isEqual";
import { useTheme } from "@mui/material/styles";
import WeeklyScheduleConfigurationModal from "layouts/pages/jobs/components/ShiftJobs/ShiftForm/WeeklyScheduleConfigurationModal";
import {
  Add as AddIcon,
  CalendarMonth,
  CalendarMonth as CalendarMonthIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Mail as MailIcon,
  People as PeopleIcon,
  QueryBuilder as Preview,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { ensureSlugs } from "utils/helpers/slugs";
import fetchSingleCustomer from "api/companies/fetchSingleCustomer";
import fetchApplicants from "layouts/pages/applicants/actions/fetchApplicants";
import CustomTextField from "components/Form/CustomTextField";
import ShiftJobSettingsModal from "./JobSettingsModal";
import ShiftSettingsModal from "./ShiftSettingsModal";
import WeeklySchedule from "./WeeklySchedule";
import DeleteShiftConfirmationDialog from "./DeleteShiftConfirmationDialog";
import ShiftPreviewAndEditPunchesModal from "./ShiftPreviewAndEditPunches/ShiftPreviewAndEditPunchesModal";
import ShiftCalendar from "./ShiftCalendarView/ShiftCalendarViewModal";
import BulkMessageShiftModal from "./BulkMessageShiftModal";

const data2 = {
  shiftName: "",
  shiftStartDate: "",
  shiftEndDate: "",
  payRate: "",
  billRate: "",
  defaultSchedule: {
    monday: { start: "", end: "" },
    tuesday: { start: "", end: "" },
    wednesday: { start: "", end: "" },
    thursday: { start: "", end: "" },
    friday: { start: "", end: "" },
    saturday: { start: "", end: "" },
    sunday: { start: "", end: "" },
  },
  exceptions: [],
  shiftRoster: [],
};

const emptyValues = {
  location: "",
  additionalConfig: {
    geofence: false,
    earlyClockInMinutes: 0,
    autoAdjustEarlyClockIn: false,
    allowManualPunches: false,
    allowOvertime: false,
    allowBreaks: false,
    allowUnaccruedLeaveRequest: false,
    allowNegativeLeave: false,
    negativeHoursLimit: 4,
    autoClockoutShiftEnd: false,
  },
  shifts: [],
};

const switchData = (watch) => [
  {
    label: "Clock-In at Shift Start",
    name: "additionalConfig.autoAdjustEarlyClockIn",
    disabled: watch("additionalConfig.earlyClockInMinutes") < 1,
  },
  { label: "Allow Employee to Edit Time", name: "additionalConfig.allowManualPunches" },
  { label: "Geofence", name: "additionalConfig.geofence" },
  {
    label: "Allow Scheduling Unaccrued PTO",
    name: "additionalConfig.allowUnaccruedLeaveRequest",
    disabled: true,
  },
  { label: "Allow Auto Clockout", name: "additionalConfig.autoClockoutShiftEnd" },
  { label: "Allow Breaks", name: "additionalConfig.allowBreaks" },
  { label: "Allow Negative Leave", name: "additionalConfig.allowNegativeLeave", disabled: true },
  { label: "Allow Overtime", name: "additionalConfig.allowOvertime" },
];

const ShiftJobs = ({ isVenue = false }) => {
  const theme = useTheme();
  const [existingData, setExistingData] = useState(emptyValues);
  const [selectedShiftIndex, setSelectedShiftIndex] = useState(null);
  const [isShiftsDirty, setShiftsDirty] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isWeeklyScheduleModalOpen, setWeeklyScheduleModalOpen] = useState(false);
  const [showHiddenShifts, setShowHiddenShifts] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [selectedShifts, setSelectedShifts] = useState(new Set());
  const [expandedSchedules, setExpandedSchedules] = useState(new Set());
  const [isShiftSettingsModalOpen, setIsShiftSettingsModalOpen] = useState(false);
  const [currentEditingShift, setCurrentEditingShift] = useState(null);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyModalData, setCopyModalData] = useState({ sourceDay: "", targetDays: [] });
  const [copyModalAnchor, setCopyModalAnchor] = useState(null);
  const [isAddShiftButtonClicked, setIsAddShiftButtonClicked] = useState(false);
  const [isStaffingPoolOpen, setIsStaffingPoolOpen] = useState(false);
  const [selectedShiftForStaffing, setSelectedShiftForStaffing] = useState(null);
  const [localShiftsForSchedule, setLocalShiftsForSchedule] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [shiftToDelete, setShiftToDelete] = useState(null);
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
  const [isShiftEditPunchesModalOpen, setIsShiftEditPunchesModalOpen] = useState(false);
  const [selectedShiftForPreview, setSelectedShiftForPreview] = useState(null);
  const [shiftFilterTab, setShiftFilterTab] = useState(0); // 0 for current, 1 for past
  const [isBulkMessageModalOpen, setIsBulkMessageModalOpen] = useState(false);
  const [selectedShiftForBulkMessage, setSelectedShiftForBulkMessage] = useState(null);
  const [shiftSearchTerm, setShiftSearchTerm] = useState(""); // <-- NEW STATE

  const {
    currentJob,
    setCurrentJob,
    isMobile,
    setCurrentFormDirty,
    isCurrentFormDirty,
    setPanelChangesOpen,
    setChangePanel,
    changePanel,
    venues,
    currentCompany,
  } = useAppContextController();

  const resolver = useYupValidationResolver(shiftsInfoSchema);
  const settingsResolver = useYupValidationResolver(shiftJobSettingsSchema);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    trigger,
    getValues,
    formState: { isDirty, dirtyFields, errors },
  } = useForm({
    defaultValues: emptyValues,
    resolver,
  });

  // Separate form for settings modal
  const {
    control: settingsControl,
    handleSubmit: settingsHandleSubmit,
    setValue: settingsSetValue,
    reset: settingsReset,
    watch: settingsWatch,
    trigger: settingsTrigger,
    getValues: settingsGetValues,
    formState: {
      isDirty: settingsIsDirty,
      dirtyFields: settingsDirtyFields,
      errors: settingsErrors,
    },
  } = useForm({
    defaultValues: {
      location: "",
      additionalConfig: {
        geofence: false,
        earlyClockInMinutes: 0,
        autoAdjustEarlyClockIn: false,
        allowManualPunches: false,
        allowOvertime: false,
        allowBreaks: false,
        allowUnaccruedLeaveRequest: false,
        allowNegativeLeave: false,
        negativeHoursLimit: 4,
        autoClockoutShiftEnd: false,
      },
    },
    resolver: settingsResolver,
  });

  const resetForm = () => {
    if (currentJob) {
      const data = {
        location: currentJob?.location || "",
        additionalConfig: currentJob.additionalConfig || emptyValues.additionalConfig,
        shifts: currentJob.shifts || [],
      };
      reset(data);
      setExistingData(data);
      setSelectedShiftIndex(null);
      setShowDetails(false);
      setShiftsDirty(false);
    }
  };

  // Initialize settings form when modal opens
  const initializeSettingsForm = () => {
    if (currentJob) {
      const settingsData = {
        location: currentJob?.location || "",
        additionalConfig: currentJob.additionalConfig || emptyValues.additionalConfig,
      };
      settingsReset(settingsData);
    }
  };

  useEffect(() => {
    resetForm();
  }, [currentJob]);

  // Initialize settings form when modal opens
  useEffect(() => {
    if (isSettingsModalOpen) {
      initializeSettingsForm();
    }
  }, [isSettingsModalOpen, currentJob]);

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync: updateJobMutation } = useMutation(isVenue ? updateJob : updateCompanyJob);
  const idName = isVenue ? "_id" : "jobId";

  const { data: currentCustomer, isLoading } = useQuery(
    ["jobCustomer", currentJob?.companySlug],
    () => {
      return fetchSingleCustomer({ slug: currentJob?.companySlug });
    },
    {
      enabled: !!currentJob?.companySlug,
    }
  );

  const handleUpdateJob = (updatedValues, isScheduleUpdate = false) => {
    const currentFormValues = getValues();
    const shiftsWithSlugs = ensureSlugs(updatedValues.shifts || [], "shiftName");

    const finalValues = {
      location: updatedValues.location || currentFormValues.location,
      additionalConfig: updatedValues.additionalConfig || currentFormValues.additionalConfig,
      shifts: shiftsWithSlugs,
    };

    updateJobMutation(
      { [idName]: currentJob?._id, data: finalValues },
      {
        onSuccess: async (res) => {
          await queryClient.invalidateQueries("company_jobs_table");
          await queryClient.invalidateQueries("jobs");
          setCurrentJob({ ...currentJob, ...finalValues });
          if (isScheduleUpdate) {
            enqueueSnackbar("Shift schedules updated successfully.", { variant: "success" });
          } else {
            enqueueSnackbar("Job has been updated.", { variant: "success" });
          }
          setShiftsDirty(false);
          setCurrentFormDirty(false);
          reset(finalValues);
          setExistingData(finalValues);
          if (selectedShiftIndex === null) setSelectedShiftIndex(finalValues?.shifts.length - 1);
        },
        onError: (err) => console.log(err),
      }
    );
  };

  const defaultScheduleISOString = (defaultSchedule) => {
    const schedule = { ...defaultSchedule };
    Object.keys(schedule).forEach((day) => {
      const { start, end } = schedule[day];
      if (start) {
        schedule[day].start = new Date(start).toISOString();
      } else schedule[day].start = "";
      if (end) {
        schedule[day].end = new Date(end).toISOString();
      } else schedule[day].end = "";
    });
    return schedule;
  };

  const handleShiftSave = (data) => {
    const modifiedData = { ...data };
    if (!modifiedData.additionalConfig.allowNegativeLeave)
      modifiedData.additionalConfig.negativeHoursLimit = 0;
    else
      modifiedData.additionalConfig.negativeHoursLimit = parseInt(
        data.additionalConfig.negativeHoursLimit,
        10
      );
    modifiedData.additionalConfig.earlyClockInMinutes = parseInt(
      data.additionalConfig.earlyClockInMinutes,
      10
    );
    modifiedData.shifts = modifiedData.shifts.map((shift) => {
      const newShift = { ...shift };
      newShift.defaultSchedule = defaultScheduleISOString(shift.defaultSchedule);
      newShift.shiftStartDate = new Date(shift.shiftStartDate).toISOString();
      newShift.shiftEndDate = new Date(shift.shiftEndDate).toISOString();
      return newShift;
    });

    console.log("modifiedData ", modifiedData);
    handleUpdateJob(modifiedData);
  };

  const addNewShift = () => {
    setSelectedShiftIndex(null);
    setShowDetails(true);
  };

  const handleRemoveShift = (idx) => {
    const updatedShifts = existingData.shifts?.filter((_, i) => i !== idx);
    if (idx === selectedShiftIndex) {
      setSelectedShiftIndex(null);
      setShowDetails(false);
    }
    handleUpdateJob({ shifts: updatedShifts });
  };

  const toggleShiftVisibility = (shiftSlug) => {
    const updatedShifts = [...existingData.shifts];
    const shiftIndex = updatedShifts.findIndex((shift) => shift.slug === shiftSlug);
    if (shiftIndex !== -1) {
      updatedShifts[shiftIndex] = {
        ...updatedShifts[shiftIndex],
        isHidden: !updatedShifts[shiftIndex].isHidden,
      };
      handleUpdateJob({ shifts: updatedShifts });
    }
  };

  const handleCopyShift = (shiftSlug) => {
    const shiftToCopy = existingData.shifts.find((shift) => shift.slug === shiftSlug);
    if (shiftToCopy) {
      const newShift = { ...shiftToCopy, shiftName: `${shiftToCopy.shiftName} (Copy)` };
      delete newShift.slug; // Remove `slug`, slug will be generated on back-end
      setExistingData((prev) => {
        const updatedShifts = [...prev.shifts, newShift];
        setValue("shifts", updatedShifts, { shouldDirty: true });
        return { ...prev, shifts: updatedShifts };
      });
      setSelectedShiftIndex(existingData.shifts.length);
      setShowDetails(true);
      setShiftsDirty(true);
    }
  };

  const assignSchedule = (shiftSlug) => {
    const shiftIndex = existingData.shifts.findIndex((shift) => shift.slug === shiftSlug);
    if (shiftIndex !== -1) {
      setSelectedShiftIndex(shiftIndex);
      setShowDetails(true);
      setWeeklyScheduleModalOpen(true);
    }
  };

  const handleConfirmChanges = async () => {
    const isValid = await trigger();
    if (isValid) {
      handleSubmit(handleShiftSave)();
      changePanel();
      setCurrentFormDirty(false);
    }
  };

  const saveChanges = async () => {
    const isValid = await trigger();
    if (isValid) {
      await handleSubmit(handleShiftSave)();
      setShiftsDirty(false);
      setCurrentFormDirty(false);
    } else {
      const errorMessages = Object.values(errors)
        .filter((error) => error?.message)
        .map((error) => error.message);

      if (errorMessages.length > 0) {
        enqueueSnackbar(
          `${"Please fix the form errors before saving like: "}${errorMessages.join(", ")}`,
          { variant: "error" }
        );
      } else {
        enqueueSnackbar("Please fix the form errors before saving.", { variant: "error" });
      }
    }
  };

  const handleCancelChanges = () => {
    reset(existingData);
    setShiftsDirty(false);
  };

  useEffect(() => {
    setCurrentFormDirty(isDirty || isShiftsDirty);
    return () => {
      setShiftsDirty(false);
      setCurrentFormDirty(false);
    };
  }, [isDirty, isShiftsDirty]);

  useEffect(() => {
    return () => {
      setCurrentFormDirty(false);
    };
  }, []);

  const selectShift = (index, row) => {
    setSelectedShiftIndex(index);
    setShowDetails(true);
  };

  const handleSettingsModalSave = async (formData) => {
    try {
      const modifiedData = { ...formData };

      if (!modifiedData.additionalConfig.allowNegativeLeave) {
        modifiedData.additionalConfig.negativeHoursLimit = 0;
      } else {
        modifiedData.additionalConfig.negativeHoursLimit = parseInt(
          modifiedData.additionalConfig.negativeHoursLimit,
          10
        );
      }

      modifiedData.additionalConfig.earlyClockInMinutes = parseInt(
        modifiedData.additionalConfig.earlyClockInMinutes,
        10
      );

      modifiedData.shifts = existingData.shifts;

      await handleUpdateJob(modifiedData);
    } catch (error) {
      console.error("Error saving settings:", error);
      throw error;
    }
  };

  // Handle settings modal save with validation
  const handleSettingsModalSaveWithValidation = async () => {
    try {
      const isValid = await settingsTrigger();
      if (isValid) {
        const formData = settingsGetValues();
        await handleSettingsModalSave(formData);
        setIsSettingsModalOpen(false);
      } else {
        const errorMessages = Object.values(settingsErrors)
          .filter((error) => error?.message)
          .map((error) => error.message);

        if (errorMessages.length > 0) {
          enqueueSnackbar(`Please fix the form errors before saving: ${errorMessages.join(", ")}`, {
            variant: "error",
          });
        } else {
          enqueueSnackbar("Please fix the form errors before saving.", { variant: "error" });
        }
      }
    } catch (error) {
      console.error("Error saving shift job settings:", error);
      enqueueSnackbar("Error saving settings.", { variant: "error" });
    }
  };

  const formatDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return "";
    const start = new Date(startDate);
    const end = new Date(endDate);
    return (
      <span>
        <strong>
          {start.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })}
        </strong>
        {" - "}
        <strong>
          {end.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })}
        </strong>
      </span>
    );
  };

  const formatDaysOfWeek = (schedule) => {
    if (!schedule) return "";
    const days = [];
    const dayMap = {
      monday: "Mon",
      tuesday: "Tue",
      wednesday: "Wed",
      thursday: "Thu",
      friday: "Fri",
      saturday: "Sat",
      sunday: "Sun",
    };

    Object.keys(dayMap).forEach((day) => {
      if (schedule[day] && (schedule[day].start || schedule[day].end)) {
        days.push(dayMap[day]);
      }
    });

    return days.join(", ");
  };

  const formatTimeRange = (schedule) => {
    if (!schedule) return "";

    if (schedule.start && schedule.end) {
      const startTime = new Date(schedule.start).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      const endTime = new Date(schedule.end).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      return (
        <span>
          <strong>{startTime}</strong>
          {" - "}
          <strong>{endTime}</strong>
        </span>
      );
    }

    const firstScheduledDay = Object.entries(schedule).find(
      ([_, day]) => day && day.start && day.end
    );
    if (!firstScheduledDay) return "";

    const [day, times] = firstScheduledDay;
    const dayName = day.charAt(0).toUpperCase() + day.slice(1, 3);
    const startTime = new Date(times.start).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    const endTime = new Date(times.end).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return (
      <span>
        {dayName}: <strong>{startTime}</strong> - <strong>{endTime}</strong>
      </span>
    );
  };

  const toggleScheduleExpand = (shiftIndex) => {
    const newExpanded = new Set(expandedSchedules);
    if (newExpanded.has(shiftIndex)) {
      newExpanded.delete(shiftIndex);
    } else {
      newExpanded.add(shiftIndex);
    }
    setExpandedSchedules(newExpanded);
  };

  const isISOString = (str) => {
    if (!str) return false;
    try {
      return new Date(str).toISOString() === str;
    } catch (e) {
      return false;
    }
  };

  const convertToISO = (time) => {
    if (!time) return "";
    return isISOString(time) ? time : new Date(time).toISOString();
  };

  const handleCheckboxChange = (shiftIndex, checked) => {
    const newSelected = new Set(selectedShifts);
    if (checked) {
      newSelected.add(shiftIndex);
    } else {
      newSelected.delete(shiftIndex);
    }
    setSelectedShifts(newSelected);
  };

  const locationList = useMemo(() => {
    if (isVenue) {
      if (!venues) return [];
      return (
        Object.values(venues)?.find((venue) => isEqual(venue.name, currentJob?.venueName))
          ?.locations || []
      );
    }
    return currentCustomer?.data?.locations || [];
  }, [venues, currentJob, isVenue, currentCustomer]);

  const defaultStaffingPool = useMemo(() => {
    if (!existingData.shifts) return [];
    return existingData?.shifts[selectedShiftIndex]?.shiftRoster ?? [];
  }, [selectedShiftIndex, existingData.shifts]);

  const allOptions = {
    fetchAll: true,
    includeVerified: true,
    filters: {
      status: "Employee",
      "jobs.jobSlug": currentJob?.jobSlug,
    },
    useOrTags: true,
    useElemMatch: true,
  };

  const {
    data: allStaffingPool,
    isLoading: isStaffingPoolLoading,
    refetch: refetchAllStaffingPool,
  } = useQuery(["shiftJobAllStaffingPool", allOptions], () => fetchApplicants(allOptions));

  const visibleShifts = useMemo(() => {
    if (!existingData?.shifts) return [];

    // First filter by hidden/visible
    let filteredShifts = showHiddenShifts
      ? existingData.shifts
      : existingData.shifts.filter((shift) => !shift.isHidden);

    // Then filter by current/past based on end date
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison

    if (shiftFilterTab === 0) {
      // Current shifts: end date >= today
      filteredShifts = filteredShifts.filter((shift) => {
        if (!shift.shiftEndDate) return true; // Include shifts without end date
        const endDate = new Date(shift.shiftEndDate);
        endDate.setHours(0, 0, 0, 0);
        return endDate >= today;
      });
    } else {
      // Past shifts: end date < today
      filteredShifts = filteredShifts.filter((shift) => {
        if (!shift.shiftEndDate) return false; // Exclude shifts without end date
        const endDate = new Date(shift.shiftEndDate);
        endDate.setHours(0, 0, 0, 0);
        return endDate < today;
      });
    }

    // Filter by search term (case-insensitive, match shiftName)
    if (shiftSearchTerm.trim() !== "") {
      const term = shiftSearchTerm.trim().toLowerCase();
      filteredShifts = filteredShifts.filter((shift) =>
        (shift.shiftName || "").toLowerCase().includes(term)
      );
    }

    return filteredShifts;
  }, [existingData.shifts, showHiddenShifts, shiftFilterTab, shiftSearchTerm]);

  const handleEditShift = (shiftSlug) => {
    const shiftData = existingData.shifts.find((shift) => shift.slug === shiftSlug);
    if (shiftData) {
      setCurrentEditingShift({
        slug: shiftSlug,
        data: shiftData,
      });
      setIsShiftSettingsModalOpen(true);
    }
  };

  const handleDeleteClick = (shiftSlug) => {
    const shiftData = existingData.shifts.find((shift) => shift.slug === shiftSlug);
    if (shiftData) {
      setShiftToDelete(shiftData);
      setDeleteConfirmOpen(true);
    }
  };

  const renderShiftItem = (shift, idx) => (
    <Box
      key={idx}
      sx={{
        display: "flex",
        alignItems: "center",
        p: 2,
        mb: 0.5,
        borderRadius: 2,
        borderBottom:
          idx < visibleShifts.length - 1 ? `1px solid ${theme.palette.grey[200]}` : "none",
        backgroundColor: shift.isHidden ? "#ffebee" : "#F5F5F5",
        opacity: shift.isHidden ? 0.7 : 1,
        cursor: "pointer",
        "&:hover": {
          backgroundColor: shift.isHidden ? "#ffcdd2" : "#eeeeee",
        },
        ...(shift.isHidden && {
          border: "1px dashed #f44336",
        }),
      }}
      onClick={() => handleCheckboxChange(idx, !selectedShifts.has(idx))}
    >
      <Checkbox
        checked={selectedShifts.has(idx)}
        onChange={(e) => {
          e.stopPropagation();
          handleCheckboxChange(idx, e.target.checked);
        }}
        sx={{
          mr: 2,
          color: "#1976d2",
          "&.Mui-checked": {
            color: "#1976d2",
          },
        }}
      />

      <Box sx={{ flex: 1 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            fontSize: "16px",
            color: shift.isHidden ? "#f44336" : "#212121",
            mb: 0.5,
          }}
        >
          {shift.shiftName || `Shift ${idx + 1}`}
          {shift.isHidden && (
            <Typography
              component="span"
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                color: "#f44336",
                ml: 1,
                fontStyle: "italic",
              }}
            >
              (Hidden)
            </Typography>
          )}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#757575",
            fontSize: "14px",
            mb: 0.25,
          }}
        >
          {formatDaysOfWeek(shift.defaultSchedule)}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#757575",
            fontSize: "14px",
            mb: 0.25,
          }}
        >
          Active: {formatDateRange(shift.shiftStartDate, shift.shiftEndDate)}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "row", top: 0, gap: 0.5 }}>
        <Tooltip title="Edit shift">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleEditShift(shift.slug);
            }}
            sx={{
              color: "#4caf50",
              "&:hover": { backgroundColor: "rgba(76, 175, 80, 0.1)" },
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Duplicate shift">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleCopyShift(shift.slug);
            }}
            sx={{
              color: "#ff9800",
              "&:hover": { backgroundColor: "rgba(255, 152, 0, 0.1)" },
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={shift.isHidden ? "Show shift" : "Hide shift"}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              toggleShiftVisibility(shift.slug);
            }}
            sx={{
              color: "#2196f3",
              "&:hover": { backgroundColor: "rgba(33, 150, 243, 0.1)" },
            }}
          >
            {shift.isHidden ? (
              <VisibilityOffIcon fontSize="small" />
            ) : (
              <VisibilityIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title="Configure weekly schedule">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              assignSchedule(shift.slug);
            }}
            sx={{
              color: "rgb(95, 56, 235)",
              "&:hover": { backgroundColor: "rgba(16, 13, 241, 0.1)" },
            }}
          >
            <CalendarMonthIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit punches">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedShiftForPreview(shift);
              setIsShiftEditPunchesModalOpen(true);
            }}
            sx={{
              color: "#1976d2",
              "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.1)" },
            }}
          >
            <Preview fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Bulk message shift employees">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedShiftForBulkMessage(shift);
              setIsBulkMessageModalOpen(true);
            }}
            sx={{
              color: "#f44336",
              "&:hover": { backgroundColor: "rgba(244, 67, 54, 0.1)" },
            }}
          >
            <MailIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Manage staffing pool">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedShiftForStaffing(shift.shiftName);
              setIsStaffingPoolOpen(true);
            }}
            sx={{
              color: "#1976d2",
              "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.1)" },
            }}
          >
            <PeopleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete shift">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(shift.slug);
            }}
            sx={{
              color: "#f44336",
              "&:hover": { backgroundColor: "rgba(244, 67, 54, 0.1)" },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  const handleShiftSettingsModalSave = async (shiftData) => {
    try {
      const updatedShifts = [...existingData.shifts];

      // Process schedule times to ISO strings
      const processedSchedule = {};
      Object.entries(shiftData.defaultSchedule || shiftData.schedule || {}).forEach(
        ([day, times]) => {
          processedSchedule[day] = {
            start: times.start ? convertToISO(times.start) : "",
            end: times.end ? convertToISO(times.end) : "",
            roster: times?.roster || [],
          };
        }
      );

      // Create the new/updated shift object
      const newShift = {
        shiftName: shiftData.shiftName,
        shiftStartDate: shiftData.shiftStartDate ? shiftData.shiftStartDate.toISOString() : null,
        shiftEndDate: shiftData.shiftEndDate ? shiftData.shiftEndDate.toISOString() : null,
        payRate: shiftData.payRate,
        billRate: shiftData.billRate,
        positions: shiftData.positions || [],
        defaultSchedule: processedSchedule,
        shiftRoster: shiftData.assignedStaff.map((staff) => ({
          _id: staff.id, // Map id back to _id
          fullName: staff.name,
          firstName: staff.firstName,
          lastName: staff.lastName,
          email: staff.email,
          payRate: staff.payRate,
          billRate: staff.billRate,
          assignedPosition: staff.assignedPosition || "",
        })),
      };

      if (isAddShiftButtonClicked) {
        // Add new shift
        updatedShifts.push(newShift);
      } else if (currentEditingShift?.data) {
        // Update existing shift
        const actualIndex = updatedShifts.findIndex(
          (shift) => shift.slug === currentEditingShift.data.slug
        );

        if (actualIndex !== -1) {
          updatedShifts[actualIndex] = {
            ...updatedShifts[actualIndex],
            ...newShift,
          };
        }
      }

      // Update the job with new shifts
      await handleUpdateJob({ shifts: updatedShifts });

      // Show success message
      // enqueueSnackbar(
      //   isAddShiftButtonClicked ? "Shift created successfully." : "Shift updated successfully.",
      //   { variant: "success" }
      // );

      // Reset states
      setCurrentEditingShift(null);
      setIsAddShiftButtonClicked(false);
      setIsShiftSettingsModalOpen(false);
    } catch (error) {
      console.error("Error saving shift:", error);
      enqueueSnackbar("Error saving shift.", { variant: "error" });
    }
  };

  const selectedShiftsForSchedule = useMemo(() => {
    if (selectedShifts.size === 0) return [];
    return visibleShifts.filter((shift, index) => selectedShifts.has(index));
  }, [visibleShifts, selectedShifts]);

  const handleDeleteConfirm = async () => {
    if (shiftToDelete) {
      const shiftIndex = existingData.shifts.findIndex(
        (shift) => shift.slug === shiftToDelete.slug
      );
      if (shiftIndex !== -1) {
        await handleRemoveShift(shiftIndex);
      }
      setDeleteConfirmOpen(false);
      setShiftToDelete(null);
    }
  };

  const handleScheduleUpdate = async (shiftData, isValidShifts) => {
    try {
      const updatedShifts = [...existingData.shifts];

      // Handle array of shifts
      const shiftsToUpdate = Array.isArray(shiftData) ? shiftData : [shiftData];

      shiftsToUpdate.forEach((shiftUpdate) => {
        if (!shiftUpdate || !shiftUpdate.slug) return; // Skip invalid shift data

        const actualIndex = updatedShifts.findIndex((shift) => shift.slug === shiftUpdate.slug);

        if (actualIndex !== -1) {
          const processedSchedule = {};
          // Add null check for schedule
          const schedule = shiftUpdate.schedule || shiftUpdate.defaultSchedule || {};

          Object.entries(schedule).forEach(([day, times]) => {
            if (!times) return; // Skip if times is null/undefined

            processedSchedule[day] = {
              start: convertToISO(times.start),
              end: convertToISO(times.end),
              roster: times?.roster || [],
            };
          });

          updatedShifts[actualIndex] = {
            ...updatedShifts[actualIndex],
            defaultSchedule: processedSchedule,
          };
        }
      });

      await handleUpdateJob({ shifts: updatedShifts }, true);
    } catch (error) {
      console.error("Error updating shift schedules:", error);
      enqueueSnackbar("Error updating shift schedules.", { variant: "error" });
    }
  };

  const handleTabChange = (event, newValue) => {
    setShiftFilterTab(newValue);
    setSelectedShifts(new Set()); // Clear selection when switching tabs
  };

  const renderEmptyStateMessage = () => {
    if (existingData.shifts?.length === 0) {
      return (
        <Typography variant="body2">
          No shifts available. Click the + button to add a new shift.
        </Typography>
      );
    }

    if (shiftFilterTab === 0) {
      return (
        <Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            No current shifts available. Click the + button to add a new shift.
          </Typography>
          <Typography variant="caption" sx={{ color: "#757575" }}>
            Current shifts are those with end dates today or in the future.
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        <Typography variant="body2" sx={{ mb: 2 }}>
          No past shifts available.
        </Typography>
        <Typography variant="caption" sx={{ color: "#757575" }}>
          Past shifts are those with end dates before today.
        </Typography>
      </Box>
    );
  };

  return (
    <form onSubmit={handleSubmit(handleShiftSave)}>
      <CustomCard
        icon={<PendingActionsIcon color="white" />}
        cardTitle="Job Shift Settings"
        cardActions={
          <Stack direction="row" spacing={2}>
            <Button
              variant="text"
              startIcon={<PeopleIcon />}
              onClick={() => setIsStaffingPoolOpen(true)}
              sx={{
                color: "#4caf50",
                fontSize: 14,
                fontWeight: 600,
                padding: "6px 12px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                "&:hover": {
                  backgroundColor: "rgba(76, 175, 80, 0.1)",
                },
              }}
            >
              STAFFING POOL
            </Button>
            <Button
              variant="text"
              onClick={() => setIsSettingsModalOpen(true)}
              startIcon={<SettingsIcon />}
              sx={{
                color: "#1976d2",
                fontSize: 14,
                fontWeight: 600,
                padding: "6px 12px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.1)",
                },
              }}
            >
              SETTINGS
            </Button>
            <Button
              variant="text"
              onClick={() => setIsCalendarModalOpen(true)}
              startIcon={<CalendarMonth />}
              sx={{
                color: "#ff9800",
                fontSize: 14,
                fontWeight: 600,
                padding: "6px 12px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                "&:hover": {
                  backgroundColor: "rgba(255, 152, 0, 0.1)",
                },
              }}
            >
              CALENDAR
            </Button>
          </Stack>
        }
        {...customPanelCard(isMobile, "success")}
      >
        <Stack spacing={4} sx={styles.container}>
          {/* Job Shifts Section */}
          <MDBox>
            {/* Filter Tabs */}
            <Box
              sx={{
                mb: 3,
                backgroundColor: "#f8f9fa",
                borderRadius: 2,
                p: 0.5,
                border: "1px solid #e9ecef",
              }}
            >
              <Tabs
                value={shiftFilterTab}
                onChange={handleTabChange}
                sx={{
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#1976d2",
                    height: 3,
                    borderRadius: "3px 3px 0 0",
                  },
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "14px",
                    minHeight: 48,
                    color: "#6c757d",
                    "&.Mui-selected": {
                      color: "#1976d2",
                    },
                    "&:hover": {
                      color: "#1976d2",
                      backgroundColor: "rgba(25, 118, 210, 0.04)",
                    },
                  },
                }}
              >
                <Tab
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: "#4caf50",
                          boxShadow: "0 0 0 2px rgba(76, 175, 80, 0.2)",
                        }}
                      />
                      <Typography sx={{ fontWeight: 600 }}>Current Shifts</Typography>
                      <Box
                        sx={{
                          backgroundColor: "#e3f2fd",
                          color: "#1976d2",
                          borderRadius: "12px",
                          px: 1.5,
                          py: 0.25,
                          fontSize: "12px",
                          fontWeight: 600,
                          minWidth: "20px",
                          textAlign: "center",
                        }}
                      >
                        {existingData.shifts?.filter((shift) => {
                          if (!shift.shiftEndDate) return true;
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const endDate = new Date(shift.shiftEndDate);
                          endDate.setHours(0, 0, 0, 0);
                          return endDate >= today;
                        }).length || 0}
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: "#ff9800",
                          boxShadow: "0 0 0 2px rgba(255, 152, 0, 0.2)",
                        }}
                      />
                      <Typography sx={{ fontWeight: 600 }}>Past Shifts</Typography>
                      <Box
                        sx={{
                          backgroundColor: "#fff3e0",
                          color: "#ff9800",
                          borderRadius: "12px",
                          px: 1.5,
                          py: 0.25,
                          fontSize: "12px",
                          fontWeight: 600,
                          minWidth: "20px",
                          textAlign: "center",
                        }}
                      >
                        {existingData.shifts?.filter((shift) => {
                          if (!shift.shiftEndDate) return false;
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const endDate = new Date(shift.shiftEndDate);
                          endDate.setHours(0, 0, 0, 0);
                          return endDate < today;
                        }).length || 0}
                      </Box>
                    </Box>
                  }
                />
              </Tabs>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              {/* Search bar and title row */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                {/* Search Bar */}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: "18px",
                    color: "#212121",
                  }}
                >
                  Job Shifts
                </Typography>
                <Box sx={{ minWidth: 320 }}>
                  <CustomTextField
                    size="small"
                    placeholder="Search shifts..."
                    value={shiftSearchTerm}
                    onChange={(e) => setShiftSearchTerm(e.target.value)}
                    InputProps={{
                      style: { background: "#fff", borderRadius: 8, fontSize: 14 },
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {/* Only show the toggle button if there are hidden shifts */}
                {existingData.shifts?.some((shift) => shift.isHidden) && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowHiddenShifts(!showHiddenShifts)}
                    sx={{
                      fontSize: "12px",
                      fontWeight: 500,
                      color: showHiddenShifts ? "#f44336" : "#1976d2",
                      textTransform: "none",
                      minWidth: "auto",
                      padding: "4px 8px",
                      "&:hover": {
                        backgroundColor: showHiddenShifts
                          ? "rgba(244, 67, 54, 0.1)"
                          : "rgba(25, 118, 210, 0.1)",
                      },
                    }}
                  >
                    {showHiddenShifts ? "Hide Hidden Shifts" : "Show All Shifts"}
                  </Button>
                )}
                <IconButton
                  onClick={() => {
                    setCurrentEditingShift(null);
                    setIsAddShiftButtonClicked(true);
                    setIsShiftSettingsModalOpen(true);
                  }}
                  sx={{
                    backgroundColor: "#1976d2",
                    color: "white",
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    boxShadow: 1,
                    "&:hover": {
                      backgroundColor: "#1565c0",
                    },
                  }}
                >
                  <AddIcon color="white" />
                </IconButton>
              </Box>
            </Box>

            <Box
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                mb: 3,
                backgroundColor: "white",
              }}
            >
              {visibleShifts.length > 0 ? (
                visibleShifts.map((shift, index) => renderShiftItem(shift, index))
              ) : (
                <Box sx={{ p: 3, textAlign: "center", color: theme.palette.text.secondary }}>
                  {renderEmptyStateMessage()}
                </Box>
              )}
            </Box>
          </MDBox>

          <WeeklySchedule
            shifts={selectedShiftsForSchedule}
            onScheduleUpdate={handleScheduleUpdate}
          />

          {isWeeklyScheduleModalOpen && (
            <WeeklyScheduleConfigurationModal
              selectedShiftIndex={selectedShiftIndex}
              getValues={getValues}
              setValue={setValue}
              isWeeklyScheduleModalOpen={isWeeklyScheduleModalOpen}
              setWeeklyScheduleModalOpen={setWeeklyScheduleModalOpen}
              saveChanges={saveChanges}
            />
          )}
        </Stack>
      </CustomCard>

      <ShiftJobSettingsModal
        open={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        onSave={handleSettingsModalSaveWithValidation}
        control={settingsControl}
        watch={settingsWatch}
        getValues={settingsGetValues}
        setValue={settingsSetValue}
        trigger={settingsTrigger}
        locationList={locationList}
        errors={settingsErrors}
      />

      <ShiftSettingsModal
        open={isShiftSettingsModalOpen}
        onClose={() => {
          setIsShiftSettingsModalOpen(false);
          setCurrentEditingShift(null);
          setIsAddShiftButtonClicked(false);
        }}
        shiftData={(() => {
          if (isAddShiftButtonClicked) {
            return {
              shiftName: "",
              shiftStartDate: null,
              shiftEndDate: null,
              payRate: "",
              billRate: "",
              positions: [],
              schedule: {},
              assignedStaff: [],
              shiftRoster: [],
              slug: "",
            };
          }
          if (currentEditingShift?.data) {
            return {
              shiftName: currentEditingShift.data.shiftName || "",
              shiftStartDate: currentEditingShift.data.shiftStartDate,
              shiftEndDate: currentEditingShift.data.shiftEndDate,
              payRate: currentEditingShift.data.payRate || "",
              billRate: currentEditingShift.data.billRate || "",
              positions: currentEditingShift.data.positions || [],
              schedule: currentEditingShift.data.defaultSchedule || {},
              assignedStaff: currentEditingShift.data.shiftRoster || [],
              slug: currentEditingShift.data.slug, // Add slug for duplicate checking
              shiftRoster: currentEditingShift.data.shiftRoster || [],
            };
          }
          return null;
        })()}
        onSave={handleShiftSettingsModalSave}
        availableStaff={
          isStaffingPoolLoading
            ? []
            : allStaffingPool?.data?.map((staff) => ({
                id: staff._id, // Map _id to id
                name: `${staff.firstName} ${staff.lastName}`, // Map fullName to name
                firstName: staff.firstName,
                lastName: staff.lastName,
                email: staff.email,
                payRate: staff.payRate || "",
                billRate: staff.billRate || "",
              })) || []
        }
        assignedStaff={(() => {
          if (isAddShiftButtonClicked) return [];
          const roster = currentEditingShift?.data?.shiftRoster || [];
          return roster.map((staff) => {
            if (staff.fullName) {
              return {
                id: staff._id,
                name: staff.fullName,
                firstName: staff.firstName,
                lastName: staff.lastName,
                email: staff.email,
                payRate: staff.payRate || "",
                billRate: staff.billRate || "",
                assignedPosition: staff.assignedPosition || "",
              };
            }
            const found = isStaffingPoolLoading
              ? null
              : allStaffingPool?.data?.find((s) => s._id === staff._id);
            return {
              id: staff._id,
              name: found?.fullName || "",
              firstName: found?.firstName || "",
              lastName: found?.lastName || "",
              email: found?.email || "",
              payRate: staff.payRate || found?.payRate || "",
              billRate: staff.billRate || found?.billRate || "",
              assignedPosition: staff.assignedPosition || "",
            };
          });
        })()}
        existingShifts={existingData.shifts || []}
        loading={isStaffingPoolLoading}
      />
      <StaffingPoolModal
        open={isStaffingPoolOpen}
        onClose={() => {
          setIsStaffingPoolOpen(false);
          setTimeout(() => {
            setSelectedShiftForStaffing(null);
          }, 300);
        }}
        isVenue={isVenue}
        initialShiftFilter={selectedShiftForStaffing}
      />
      <ShiftCalendar
        isModalOpen={isCalendarModalOpen}
        setIsModalOpen={setIsCalendarModalOpen}
        shifts={existingData.shifts || []}
        employees={isStaffingPoolLoading ? [] : allStaffingPool?.data || []}
        onUpdate={handleUpdateJob}
        job={currentJob}
        isLoading={isStaffingPoolLoading}
      />

      {deleteConfirmOpen && (
        <DeleteShiftConfirmationDialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
          onConfirm={handleDeleteConfirm}
          jobId={currentJob?._id}
          shiftData={shiftToDelete}
        />
      )}

      {isShiftEditPunchesModalOpen && (
        <ShiftPreviewAndEditPunchesModal
          shift={selectedShiftForPreview}
          isShiftEditPunchesModalOpen={isShiftEditPunchesModalOpen}
          setShiftEditPunchesModalOpen={setIsShiftEditPunchesModalOpen}
        />
      )}
      {isBulkMessageModalOpen && (
        <BulkMessageShiftModal
          open={isBulkMessageModalOpen}
          onClose={() => {
            setIsBulkMessageModalOpen(false);
            setSelectedShiftForBulkMessage(null);
          }}
          shift={selectedShiftForBulkMessage}
          job={currentJob}
          allStaffingPool={isStaffingPoolLoading ? [] : allStaffingPool?.data || []}
          isStaffingPoolLoading={isStaffingPoolLoading}
        />
      )}
    </form>
  );
};

export default ShiftJobs;
